// src/Components/Admin/KmlAssignData.js

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

const DARK_BLUE = '#00008B';
const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };

const BASE_TARGET_OPTIONS = [
  'ICON',
  'POSITION (LAT, LNG)',
  'VISIBILITY',
  'HOVER PHOTO',
  'HOVER TEXT',
  'INFO',
  'DESCRIPTION',
];

const INFO_OPTIONS = [
  'DATE',
  'LOCATION ID',
  'TITLE',
  'WEB SITE',
  'LOCATION ADDRESS',
  'CITY',
  'STATE',
  'POSTCODE/ZIP',
  'COUNTRY',
  'CLIENT NAME',
  'CATEGORY',
  'REGION',
  'CONTROLLER',
  'KEYWORDS',
  'SHOWCASE',
];

const VISIBILITY_OPTIONS = ['PUBLIC', 'PUBLIC FROM', 'RESTRICTED', 'PRIVATE'];

const TEXT_FIELDS = [
  'LOCATION ID',
  'TITLE',
  'WEB SITE',
  'LOCATION ADDRESS',
  'CITY',
  'STATE',
  'POSTCODE/ZIP',
  'COUNTRY',
  'CLIENT NAME',
  'CONTROLLER',
  'KEYWORDS',
  'SHOWCASE',
];

const normalizeLooseString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

const getIconUrl = (iconObj) => {
  if (!iconObj) return null;
  return (
    iconObj.url ||
    iconObj.imageUrl ||
    iconObj.iconUrl ||
    iconObj.downloadURL ||
    iconObj.image ||
    iconObj.src ||
    iconObj.fileUrl ||
    (typeof iconObj.icon === 'string' ? iconObj.icon : null)
  );
};

const handleGlobalInvertedKeyDown = (e, makeUppercase) => {
  if (makeUppercase && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    const keyLower = e.key.toLowerCase();
    const keyUpper = e.key.toUpperCase();
    if (keyLower !== keyUpper) {
      e.preventDefault();
      e.stopPropagation();
      const char = e.shiftKey ? keyLower : keyUpper;

      const input = e.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const val = input.value;
      const newVal = val.slice(0, start) + char + val.slice(end);

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      ).set;
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, newVal);
        const ev = new Event('input', { bubbles: true });
        input.dispatchEvent(ev);
      }

      setTimeout(() => {
        input.selectionStart = input.selectionEnd = start + 1;
      }, 0);
    }
  }
};

const handleGlobalInvertedPaste = (e, makeUppercase) => {
  if (makeUppercase) {
    e.preventDefault();
    e.stopPropagation();
    const paste = (e.clipboardData || window.clipboardData).getData('text').toUpperCase();

    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const val = input.value;
    const newVal = val.slice(0, start) + paste + val.slice(end);

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    ).set;
    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(input, newVal);
      const ev = new Event('input', { bubbles: true });
      input.dispatchEvent(ev);
    }

    setTimeout(() => {
      input.selectionStart = input.selectionEnd = start + paste.length;
    }, 0);
  }
};

export default function KmlAssignData({
  parsedData,
  selectedImportIds,
  categories,
  regions,
  globalCategory,
  setGlobalCategory,
  customRows,
  handleCustomRowNameChange,
  markerIcons,
  selectedCustomIds,
  setSelectedCustomIds,
  rowMappings,
  setRowMappings,
  rowSubMappings,
  setRowSubMappings,
  rowStaticValues,
  setRowStaticValues,
  defaultPublicFromDate,
  makeUppercase,
}) {
  const hasParsedData = parsedData && parsedData.length > 0;

  // Extract intrinsic properties from the KML dataset
  const hasName = hasParsedData && parsedData.some((r) => !!r.name);
  const hasRichText = hasParsedData && parsedData.some((r) => !!r.hasRichText);

  // Extract color values present in the data
  const uniqueColors = new Set();
  if (hasParsedData) {
    parsedData.forEach((r) => {
      if (r.color) uniqueColors.add(r.color.toLowerCase());
    });
  }

  const intrinsicRows = [];
  if (hasName) {
    intrinsicRows.push({ id: 'prop_name', name: 'Name', isCustom: false });
  }

  Array.from(uniqueColors).forEach((col) => {
    const colorHex = col.startsWith('#') ? col.substring(1) : col;
    intrinsicRows.push({
      id: `prop_color_${colorHex}`,
      name: `Color #${colorHex}`,
      displayColor: `#${colorHex}`,
      isCustom: false,
    });
  });

  if (hasRichText) {
    intrinsicRows.push({
      id: 'prop_richtext',
      name: 'Rich Text',
      isCustom: false,
    });
  }

  const allProperties = [...intrinsicRows, ...customRows];

  const historicalNames = new Set(['Name', 'Rich Text', 'Color']);
  if (customRows) {
    customRows.forEach((cr) => {
      if (cr.name && cr.name !== '-') historicalNames.add(cr.name);
    });
  }

  const seenNorms = new Set();
  const autocompleteOptions = [];

  Array.from(historicalNames).forEach((opt) => {
    const displayVal = makeUppercase ? opt.toUpperCase() : opt;
    const norm = normalizeLooseString(displayVal);
    if (!seenNorms.has(norm)) {
      seenNorms.add(norm);
      autocompleteOptions.push(displayVal);
    }
  });

  autocompleteOptions.sort((a, b) => a.localeCompare(b));

  const isAllSelected =
    allProperties.length > 0 && selectedCustomIds.length === allProperties.length;
  const isIndeterminate =
    selectedCustomIds.length > 0 && selectedCustomIds.length < allProperties.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCustomIds(allProperties.map((r) => r.id));
    } else {
      setSelectedCustomIds([]);
    }
  };

  const handleCheckboxToggle = (id) => {
    setSelectedCustomIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id],
    );
  };

  const handleMappingChange = (id, value) => {
    setRowMappings((prev) => {
      const next = { ...prev };
      if (value === '') {
        delete next[id];
      } else {
        next[id] = value;
      }
      return next;
    });

    setRowSubMappings((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    if (setRowStaticValues) {
      setRowStaticValues((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleSubMappingChange = (id, value) => {
    setRowSubMappings((prev) => {
      const next = { ...prev };
      if (value === '') {
        delete next[id];
      } else {
        next[id] = value;
      }
      return next;
    });

    if (setRowStaticValues) {
      setRowStaticValues((prev) => {
        const next = { ...prev };
        if (rowMappings[id] === 'VISIBILITY' && value === 'PUBLIC FROM') {
          next[id] = defaultPublicFromDate;
        } else {
          delete next[id];
        }
        return next;
      });
    }
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flexGrow: 1,
        width: '100%',
        boxSizing: 'border-box',
        borderTop: '2px solid #2e7d32',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
      }}>
      <Table stickyHeader size='small' sx={{ width: '100%', tableLayout: 'auto' }}>
        <TableHead>
          <TableRow
            sx={{
              height: '28px',
              '& th': {
                backgroundColor: '#e8f5e9',
                fontWeight: 'bold',
                borderBottom: '2px solid #2e7d32',
                py: '2px',
                ...cellStyle,
              },
            }}>
            <TableCell
              padding='checkbox'
              sx={{
                width: '64px',
                minWidth: '64px',
                maxWidth: '64px',
                textAlign: 'center',
              }}>
              <Checkbox
                size='small'
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
                sx={{ p: 0.5, '&.Mui-checked': { color: '#2e7d32' } }}
              />
            </TableCell>
            <TableCell align='left' sx={{ width: '25%' }}>
              Property Name
            </TableCell>
            <TableCell align='left' sx={{ width: '25%' }}>
              Assigned To
            </TableCell>
            <TableCell align='left' sx={{ width: '25%' }}>
              Selection
            </TableCell>
            <TableCell align='left' sx={{ width: '25%' }}>
              Value
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {allProperties.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align='center' sx={{ py: 3 }}>
                <Typography
                  color='text.secondary'
                  variant='body2'
                  sx={{ fontStyle: 'italic' }}>
                  No properties configured in this template. Please click the "Add New"
                  icon to create a row.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            allProperties.map((row) => {
              const isSelected = selectedCustomIds.includes(row.id);
              const mappedTarget = rowMappings[row.id];

              const displayName =
                makeUppercase && typeof row.name === 'string'
                  ? row.name.toUpperCase()
                  : row.name;

              return (
                <TableRow
                  key={row.id}
                  hover
                  selected={isSelected}
                  onClick={() => handleCheckboxToggle(row.id)}
                  sx={{
                    height: '34px',
                    cursor: 'pointer',
                    '& td': { py: '4px', ...cellStyle },
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&.Mui-selected': { backgroundColor: 'transparent !important' },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                    },
                  }}>
                  <TableCell
                    padding='checkbox'
                    sx={{
                      width: '64px',
                      minWidth: '64px',
                      maxWidth: '64px',
                      textAlign: 'center',
                    }}>
                    <Checkbox
                      checked={isSelected}
                      size='small'
                      sx={{
                        pointerEvents: 'none',
                        p: 0.5,
                        '&.Mui-checked': { color: '#2e7d32' },
                      }}
                    />
                  </TableCell>
                  <TableCell align='left'>
                    {row.isCustom ? (
                      <Autocomplete
                        freeSolo
                        disableClearable
                        options={autocompleteOptions}
                        slotProps={{ popper: { sx: { zIndex: 100020 } } }}
                        inputValue={row.name === '-' ? '' : displayName || ''}
                        onInputChange={(event, newInputValue) => {
                          const val =
                            makeUppercase && newInputValue
                              ? newInputValue.toUpperCase()
                              : newInputValue;
                          handleCustomRowNameChange(row.id, val || '-');
                        }}
                        renderInput={(params) => {
                          const origOnKeyDown = params.inputProps.onKeyDown;
                          return (
                            <TextField
                              {...params}
                              size='small'
                              placeholder='Enter Property...'
                              onClick={(e) => e.stopPropagation()}
                              inputProps={{
                                ...params.inputProps,
                                onKeyDown: (e) => {
                                  handleGlobalInvertedKeyDown(e, makeUppercase);
                                  if (!e.defaultPrevented && origOnKeyDown) {
                                    origOnKeyDown(e);
                                  }
                                },
                                onPaste: (e) =>
                                  handleGlobalInvertedPaste(e, makeUppercase),
                              }}
                              sx={{
                                backgroundColor: '#e8f5e9',
                                borderRadius: '4px',
                                '& .MuiOutlinedInput-root': {
                                  padding: '0 4px',
                                  minHeight: '26px',
                                  height: '26px',
                                  '& fieldset': {
                                    borderColor: '#a5d6a7',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#2e7d32',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#2e7d32',
                                  },
                                },
                                '& .MuiInputBase-input': {
                                  color: DARK_BLUE,
                                  fontSize: '0.875rem',
                                  py: '0px',
                                  px: '4px',
                                  textTransform: makeUppercase ? 'uppercase' : 'none',
                                },
                              }}
                            />
                          );
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant='body2'
                          sx={{ color: DARK_BLUE, fontWeight: 'bold' }}>
                          {row.displayColor
                            ? makeUppercase
                              ? 'COLOR'
                              : 'Color'
                            : displayName}
                        </Typography>
                        {row.displayColor && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: row.displayColor,
                              border: '1px solid rgba(0,0,0,0.2)',
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align='left' onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={rowMappings[row.id] || ''}
                      onChange={(e) => handleMappingChange(row.id, e.target.value)}
                      displayEmpty
                      size='small'
                      fullWidth
                      sx={{
                        height: '26px',
                        backgroundColor: '#fff',
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {
                          py: '0px',
                          minHeight: '26px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                      MenuProps={{ style: { zIndex: 100020 } }}>
                      <MenuItem value=''>
                        <em style={{ color: '#999' }}>Select mapping...</em>
                      </MenuItem>
                      {BASE_TARGET_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align='left' onClick={(e) => e.stopPropagation()}>
                    {mappedTarget === 'INFO' ? (
                      <Select
                        value={rowSubMappings[row.id] || ''}
                        onChange={(e) => handleSubMappingChange(row.id, e.target.value)}
                        displayEmpty
                        size='small'
                        fullWidth
                        sx={{
                          height: '26px',
                          backgroundColor: '#fff',
                          fontSize: '0.875rem',
                          '& .MuiSelect-select': {
                            py: '0px',
                            minHeight: '26px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                        MenuProps={{ style: { zIndex: 100020 } }}>
                        <MenuItem value=''>
                          <em style={{ color: '#999' }}>Select Info field...</em>
                        </MenuItem>
                        {INFO_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : mappedTarget === 'ICON' ? (
                      <Select
                        value={
                          markerIcons?.some((i) => i.id === rowSubMappings[row.id])
                            ? rowSubMappings[row.id]
                            : ''
                        }
                        onChange={(e) => handleSubMappingChange(row.id, e.target.value)}
                        displayEmpty
                        size='small'
                        fullWidth
                        sx={{
                          height: '26px',
                          backgroundColor: '#fff',
                          fontSize: '0.875rem',
                          '& .MuiSelect-select': {
                            py: '0px',
                            minHeight: '26px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                        MenuProps={{ style: { zIndex: 100020 } }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <em style={{ color: '#999' }}>Select Icon...</em>;
                          }
                          const selectedIcon = markerIcons?.find(
                            (icon) => icon.id === selected,
                          );
                          if (!selectedIcon) return selected;

                          const iconImgUrl = getIconUrl(selectedIcon);

                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {iconImgUrl && (
                                <img
                                  src={iconImgUrl}
                                  alt={selectedIcon.name || 'icon'}
                                  style={{ width: 18, height: 18, objectFit: 'contain' }}
                                />
                              )}
                              <span>{selectedIcon.name || 'Unnamed Icon'}</span>
                            </Box>
                          );
                        }}>
                        <MenuItem value=''>
                          <em style={{ color: '#999' }}>Select Icon...</em>
                        </MenuItem>
                        {markerIcons &&
                          markerIcons.map((icon) => {
                            const iconImgUrl = getIconUrl(icon);

                            return (
                              <MenuItem
                                key={icon.id}
                                value={icon.id}
                                sx={{
                                  fontSize: '0.875rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}>
                                {iconImgUrl && (
                                  <img
                                    src={iconImgUrl}
                                    alt={icon.name || 'icon'}
                                    style={{
                                      width: 18,
                                      height: 18,
                                      objectFit: 'contain',
                                    }}
                                  />
                                )}
                                {icon.name || 'Unnamed Icon'}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    ) : mappedTarget === 'VISIBILITY' ? (
                      <Select
                        value={rowSubMappings[row.id] || ''}
                        onChange={(e) => handleSubMappingChange(row.id, e.target.value)}
                        displayEmpty
                        size='small'
                        fullWidth
                        sx={{
                          height: '26px',
                          backgroundColor: '#fff',
                          fontSize: '0.875rem',
                          '& .MuiSelect-select': {
                            py: '0px',
                            minHeight: '26px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                        MenuProps={{ style: { zIndex: 100020 } }}>
                        <MenuItem value=''>
                          <em style={{ color: '#999' }}>Select Visibility...</em>
                        </MenuItem>
                        {VISIBILITY_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography variant='caption' color='text.disabled'>
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align='left' onClick={(e) => e.stopPropagation()}>
                    {row.name !== '-' ? (
                      mappedTarget === 'INFO' ? (
                        rowSubMappings[row.id] === 'DATE' ? (
                          <TextField
                            type='date'
                            size='small'
                            value={rowStaticValues[row.id] || ''}
                            onChange={(e) => {
                              if (setRowStaticValues) {
                                const val = makeUppercase
                                  ? e.target.value.toUpperCase()
                                  : e.target.value;
                                setRowStaticValues((prev) => ({
                                  ...prev,
                                  [row.id]: val,
                                }));
                              }
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              width: '100%',
                              height: '26px',
                              backgroundColor: '#fff',
                              '& .MuiInputBase-root': { height: '26px' },
                              '& .MuiInputBase-input': { py: '0px', px: '8px' },
                            }}
                          />
                        ) : rowSubMappings[row.id] === 'CATEGORY' ? (
                          <Select
                            size='small'
                            displayEmpty
                            value={
                              categories?.some((c) => c.id === rowStaticValues[row.id])
                                ? rowStaticValues[row.id]
                                : ''
                            }
                            onChange={(e) => {
                              if (setRowStaticValues) {
                                setRowStaticValues((prev) => ({
                                  ...prev,
                                  [row.id]: e.target.value,
                                }));
                              }
                            }}
                            sx={{
                              width: '100%',
                              height: '26px',
                              backgroundColor: '#fff',
                              fontSize: '0.875rem',
                              '& .MuiSelect-select': {
                                py: '0px',
                                minHeight: '26px',
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                            MenuProps={{ style: { zIndex: 100020 } }}>
                            <MenuItem value='' disabled>
                              <em style={{ color: '#999' }}>Select Category...</em>
                            </MenuItem>
                            {categories &&
                              categories.map((cat) => (
                                <MenuItem
                                  key={cat.id}
                                  value={cat.id}
                                  sx={{ fontSize: '0.875rem' }}>
                                  {cat.name}
                                </MenuItem>
                              ))}
                          </Select>
                        ) : rowSubMappings[row.id] === 'REGION' ? (
                          <Select
                            size='small'
                            displayEmpty
                            value={
                              regions?.some((r) => r.id === rowStaticValues[row.id])
                                ? rowStaticValues[row.id]
                                : ''
                            }
                            onChange={(e) => {
                              if (setRowStaticValues) {
                                setRowStaticValues((prev) => ({
                                  ...prev,
                                  [row.id]: e.target.value,
                                }));
                              }
                            }}
                            sx={{
                              width: '100%',
                              height: '26px',
                              backgroundColor: '#fff',
                              fontSize: '0.875rem',
                              '& .MuiSelect-select': {
                                py: '0px',
                                minHeight: '26px',
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                            MenuProps={{ style: { zIndex: 100020 } }}>
                            <MenuItem value='' disabled>
                              <em style={{ color: '#999' }}>Select Region...</em>
                            </MenuItem>
                            {regions &&
                              regions.map((reg) => (
                                <MenuItem
                                  key={reg.id}
                                  value={reg.id}
                                  sx={{ fontSize: '0.875rem' }}>
                                  {reg.name}
                                </MenuItem>
                              ))}
                          </Select>
                        ) : TEXT_FIELDS.includes(rowSubMappings[row.id]) ? (
                          <TextField
                            size='small'
                            placeholder='Enter value'
                            value={rowStaticValues[row.id] || ''}
                            onChange={(e) => {
                              if (setRowStaticValues) {
                                const val = makeUppercase
                                  ? e.target.value.toUpperCase()
                                  : e.target.value;
                                setRowStaticValues((prev) => ({
                                  ...prev,
                                  [row.id]: val,
                                }));
                              }
                            }}
                            inputProps={{
                              onKeyDown: (e) =>
                                handleGlobalInvertedKeyDown(e, makeUppercase),
                              onPaste: (e) => handleGlobalInvertedPaste(e, makeUppercase),
                            }}
                            sx={{
                              width: '100%',
                              height: '26px',
                              backgroundColor: '#fff',
                              '& .MuiInputBase-root': { height: '26px' },
                              '& .MuiInputBase-input': {
                                py: '0px',
                                px: '8px',
                                textTransform: makeUppercase ? 'uppercase' : 'none',
                              },
                            }}
                          />
                        ) : null
                      ) : mappedTarget === 'VISIBILITY' &&
                        rowSubMappings[row.id] === 'PUBLIC FROM' ? (
                        <TextField
                          type='date'
                          size='small'
                          value={rowStaticValues[row.id] || ''}
                          onChange={(e) => {
                            if (setRowStaticValues) {
                              const val = makeUppercase
                                ? e.target.value.toUpperCase()
                                : e.target.value;
                              setRowStaticValues((prev) => ({
                                ...prev,
                                [row.id]: val,
                              }));
                            }
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            width: '100%',
                            height: '26px',
                            backgroundColor: '#fff',
                            '& .MuiInputBase-root': { height: '26px' },
                            '& .MuiInputBase-input': { py: '0px', px: '8px' },
                          }}
                        />
                      ) : null
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
